import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getClubBirthdayAvailabilityApi,
  createClubBirthdayReservationApi,
  createClubBirthdayRequestApi,
} from "../../api/api";
import { resolveUrl } from "../reservas/components/ReservaShared";
import {
  EMPTY_FORM,
  birthdayDateOptions,
  buildBirthdayPayload,
  classifyBookingError,
  computeAmounts,
  courtNameFor,
  enabledQuestions,
  isDepositBlocked,
  rememberRequestClub,
  validateDetailsForm,
} from "./birthdayModel";

// Selection saved before the login redirect, restored on return (same mechanism as the court flow).
export const BIRTHDAY_PENDING_KEY = "birthday_flow:pending_selection";

const readPending = (clubId) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(BIRTHDAY_PENDING_KEY) || "null");
    return saved && String(saved.clubId) === String(clubId) ? saved : null;
  } catch (_) {
    return null;
  }
};

/*
 * State machine of the public birthday flow (/canchas/:clubId/cumpleanos).
 *
 * Steps: "paquete" -> "fecha" -> "detalles", then a terminal screen:
 *   - "enviada"    approval mode: the request was sent (nothing is paid or held yet);
 *   - "confirmada" direct mode without deposit: the booking is already confirmed.
 * Direct mode with a deposit never gets to a terminal step: it redirects to Fiserv (`paymentHref`), exactly
 * like the court checkout, and the gateway returns to the existing /reservas/success|cancel pages.
 *
 * The court is never chosen by the buyer: the back assigns one, and the hook only shows the name of the first
 * free court of the chosen start.
 */
export function useBirthdayFlow({ clubId, club, courts = [], token, authUser = {}, onRequireLogin }) {
  const config = club?.birthdays || null;
  const mode = config?.mode === "direct" ? "direct" : "approval";
  const questions = config?.questions;

  // Back from the login redirect: restore the saved selection and go straight to the details step.
  const pendingRef = useRef(undefined);
  if (pendingRef.current === undefined) pendingRef.current = token ? readPending(clubId) : null;
  const pending = pendingRef.current;

  const [step, setStep] = useState(pending?.packageId && pending?.slotStart ? "detalles" : "paquete");
  const [packageId, setPackageId] = useState(pending?.packageId || null);
  const [date, setDate] = useState(pending?.date || null);
  const [slotStart, setSlotStart] = useState(pending?.slotStart || null);
  const [availability, setAvailability] = useState({ status: "idle", starts: [], slots: [], error: "" });
  const [reloadKey, setReloadKey] = useState(0);
  const [slotNotice, setSlotNotice] = useState("");

  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    name: authUser?.name || "",
    email: authUser?.email || "",
    dni: authUser?.dni || "",
  }));
  const editedRef = useRef(new Set());
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const packages = useMemo(() => (Array.isArray(config?.packages) ? config.packages : []), [config]);
  const selectedPackage = useMemo(
    () => packages.find((p) => String(p._id) === String(packageId)) || null,
    [packages, packageId]
  );
  const dateOptions = useMemo(() => birthdayDateOptions(config), [config]);
  const amounts = useMemo(
    () => computeAmounts(selectedPackage?.price, config?.depositPercent),
    [selectedPackage, config]
  );
  const depositBlocked = isDepositBlocked(config);

  // Availability of the chosen package on the chosen date. Re-runs on `reload()` (after a taken start).
  useEffect(() => {
    if (!clubId || !packageId || !date) {
      setAvailability({ status: "idle", starts: [], slots: [], error: "" });
      return undefined;
    }
    let active = true;
    setAvailability({ status: "loading", starts: [], slots: [], error: "" });
    getClubBirthdayAvailabilityApi(clubId, { date, packageId })
      .then((response) => {
        if (!active) return;
        const info = response?.data?.info || {};
        setAvailability({
          status: "ready",
          starts: Array.isArray(info.starts) ? info.starts : [],
          slots: Array.isArray(info.slots) ? info.slots : [],
          error: "",
        });
      })
      .catch((err) => {
        if (!active) return;
        setAvailability({
          status: "error",
          starts: [],
          slots: [],
          error: err?.response?.data?.message || "No pudimos cargar los horarios. Probá de nuevo.",
        });
      });
    return () => {
      active = false;
    };
  }, [clubId, packageId, date, reloadKey]);

  const selectedStart = useMemo(
    () => availability.starts.find((s) => s.slotStart === slotStart) || null,
    [availability.starts, slotStart]
  );
  const courtId = selectedStart?.courtIds?.[0] || null;
  const courtName = courtNameFor(courtId, courts);

  const selectPackage = useCallback(
    (id) => {
      if (String(id) === String(packageId)) return;
      setPackageId(id);
      setSlotStart(null);
      setSlotNotice("");
    },
    [packageId]
  );
  const selectDate = useCallback((value) => {
    setDate(value);
    setSlotStart(null);
    setSlotNotice("");
  }, []);
  const selectStart = useCallback(
    (value) => {
      if (!availability.starts.some((s) => s.slotStart === value)) return;
      setSlotStart(value);
      setSlotNotice("");
    },
    [availability.starts]
  );
  const reloadAvailability = useCallback(() => setReloadKey((k) => k + 1), []);

  const setField = useCallback((key, value) => {
    editedRef.current.add(key);
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    if (pending) {
      try {
        sessionStorage.removeItem(BIRTHDAY_PENDING_KEY);
      } catch (_) {}
    }
  }, [pending]);

  // Prefill from the logged user (also when it arrives after mount), never over what the buyer already typed.
  useEffect(() => {
    setForm((prev) => {
      const next = { ...prev };
      ["name", "email", "dni"].forEach((key) => {
        if (!editedRef.current.has(key) && !prev[key] && authUser?.[key]) next[key] = authUser[key];
      });
      return next;
    });
  }, [authUser?.name, authUser?.email, authUser?.dni]); // eslint-disable-line react-hooks/exhaustive-deps

  // A restored slot that is gone by now sends the buyer back to pick another one.
  useEffect(() => {
    if (pending && step === "detalles" && availability.status === "ready" && !selectedStart) {
      setSlotNotice("El horario que elegiste ya no está disponible. Elegí otro.");
      setStep("fecha");
    }
  }, [pending, step, availability.status, selectedStart]);

  const canContinue = step === "paquete" ? Boolean(selectedPackage) : step === "fecha" ? Boolean(selectedStart) : false;
  const goNext = useCallback(() => {
    if (!canContinue) return undefined;
    if (step === "fecha" && !token) {
      // Auth gate before the details step: keep the selection and send the buyer to login.
      try {
        sessionStorage.setItem(BIRTHDAY_PENDING_KEY, JSON.stringify({ clubId, packageId, date, slotStart }));
      } catch (_) {}
      if (onRequireLogin) onRequireLogin();
      return { unauthenticated: true };
    }
    setStep((prev) => (prev === "paquete" ? "fecha" : prev === "fecha" ? "detalles" : prev));
    return undefined;
  }, [canContinue, step, token, clubId, packageId, date, slotStart, onRequireLogin]);
  /** @returns {boolean} false when already on the first step (the page leaves the flow). */
  const goBack = useCallback(() => {
    if (step === "detalles") {
      setStep("fecha");
      return true;
    }
    if (step === "fecha") {
      setStep("paquete");
      return true;
    }
    return false;
  }, [step]);

  const errors = useMemo(
    () => validateDetailsForm(form, questions, policyAccepted),
    [form, questions, policyAccepted]
  );
  const visibleErrors = showErrors ? errors : {};

  const submit = useCallback(async () => {
    setShowErrors(true);
    if (depositBlocked || submitting || !selectedStart) return;
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setSubmitError("");
    let redirected = false;
    try {
      const payload = buildBirthdayPayload({
        selection: { packageId, date, slotStart, courtId },
        form,
        questions,
        mode,
        origin: typeof window !== "undefined" ? window.location.origin : "",
      });

      if (mode === "approval") {
        const response = await createClubBirthdayRequestApi(clubId, payload, token);
        const info = response?.data?.info || {};
        rememberRequestClub(info.token, clubId);
        setResult({ type: "request", token: info.token, respondBy: info.respondBy });
        setStep("enviada");
        return;
      }

      const response = await createClubBirthdayReservationApi(clubId, payload, token);
      const info = response?.data?.info || {};
      try {
        // Same key the court checkout writes: the shared success/cancel pages read it after the gateway returns.
        sessionStorage.setItem(
          "reservation:last",
          JSON.stringify({
            flow: "canchas",
            reservationCode: info.reservationCode,
            barId: clubId,
            courtId,
            date,
            slotStart,
            durationMinutes: selectedPackage?.durationMinutes,
          })
        );
      } catch (_) {
        // Storage can be unavailable (private mode); the redirect still works.
      }
      if (info.paymentHref) {
        redirected = true;
        window.location.href = resolveUrl(info.paymentHref);
        return;
      }
      setResult({ type: "reservation", ...info });
      setStep("confirmada");
    } catch (err) {
      const failure = classifyBookingError(err);
      if (failure.kind === "slot") {
        // The start was taken meanwhile: back to the date step with fresh availability.
        setSlotStart(null);
        setSlotNotice(failure.message);
        setStep("fecha");
        reloadAvailability();
      } else {
        setSubmitError(failure.message);
      }
    } finally {
      if (!redirected) setSubmitting(false);
    }
  }, [
    depositBlocked,
    submitting,
    selectedStart,
    errors,
    packageId,
    date,
    slotStart,
    courtId,
    form,
    questions,
    mode,
    clubId,
    token,
    selectedPackage,
    reloadAvailability,
  ]);

  return {
    config,
    mode,
    step,
    packages,
    selectedPackage,
    dateOptions,
    date,
    slotStart,
    availability,
    selectedStart,
    courtId,
    courtName,
    slotNotice,
    amounts,
    depositBlocked,
    enabledQuestions: enabledQuestions(questions),
    form,
    setField,
    policyAccepted,
    setPolicyAccepted,
    errors: visibleErrors,
    submitting,
    submitError,
    result,
    canContinue,
    selectPackage,
    selectDate,
    selectStart,
    reloadAvailability,
    goNext,
    goBack,
    submit,
  };
}
